/**
 * Display labels for workflow node types. The JSON / persisted `node.type`
 * discriminator stays as-is (e.g. `'login'`) — only the human-facing label
 * changes here. Centralizing this lets us rename copy in one place.
 */
export const NODE_TYPE_LABEL = {
  login: 'Acting As',
  navigate: 'Navigate',
  action: 'Action',
  assertion: 'Assertion',
  condition: 'Condition',
  wait: 'Wait',
  api_sequence: 'API Sequence',
  seed: 'Seed Data',
  setup: 'Setup Flow',
  notification: 'Notification',
  audio: 'Audio input',
  'audio-assertion': 'Audio assertion',
  sandbox_action: 'Sandbox action',
  mcp: 'MCP',
  chat_scenario: 'Chat scenario',
  end: 'End',
} as const;

export type NodeTypeKey = keyof typeof NODE_TYPE_LABEL;
export type NodeTypeLabel = (typeof NODE_TYPE_LABEL)[NodeTypeKey];

/**
 * Coarse grouping used to organize node types in the "Insert step" menu.
 * `common` are the everyday building blocks, `data` provisions / drives data,
 * and `advanced` covers control flow and specialty steps.
 */
export type NodeTypeCategory = 'common' | 'data' | 'advanced';

/** Human-readable section title for each {@link NodeTypeCategory}. */
export const NODE_TYPE_CATEGORY_LABEL: Record<NodeTypeCategory, string> = {
  common: 'Common',
  data: 'Data management',
  advanced: 'Advanced',
};

/** Render order for the categories in the menu. */
export const NODE_TYPE_CATEGORY_ORDER: NodeTypeCategory[] = ['common', 'data', 'advanced'];

/**
 * Per-type metadata for the node picker: a concise one-line description and the
 * section it belongs to. Keyed by the same persisted discriminator as
 * {@link NODE_TYPE_LABEL}.
 */
export const NODE_TYPE_META: Record<NodeTypeKey, { description: string; category: NodeTypeCategory }> = {
  login: { description: 'Sign in as a test user', category: 'common' },
  navigate: { description: 'Go to a URL', category: 'common' },
  action: { description: 'Click, type, or interact', category: 'common' },
  assertion: { description: 'Check something on the page', category: 'common' },
  seed: { description: 'Provision test data first', category: 'data' },
  sandbox_action: { description: 'Run a server-side sandbox script', category: 'data' },
  api_sequence: { description: 'Call API endpoints, no browser', category: 'data' },
  mcp: { description: 'Drive an MCP server, no browser', category: 'data' },
  chat_scenario: {
    description: 'Scripted multi-turn chat conversation judged against invariants',
    category: 'data',
  },
  condition: { description: 'Branch on if/else', category: 'advanced' },
  wait: { description: 'Pause before continuing', category: 'advanced' },
  notification: { description: 'Send a Slack or email alert', category: 'advanced' },
  audio: { description: 'Send spoken audio input', category: 'advanced' },
  'audio-assertion': { description: 'Verify audio output', category: 'advanced' },
  // Present for completeness; not surfaced in the edge menu's visibleTypes.
  setup: { description: 'Run another flow first', category: 'advanced' },
  end: { description: 'End the workflow', category: 'advanced' },
};

/**
 * Normalizes legacy/alias node-type keywords to the canonical persisted type.
 * Accepts `'acting-as'` / `'acting_as'` as aliases for `'login'` so CLI
 * callers can use the new name while persisted data stays `'login'`.
 */
export function normalizeNodeType(input: string): string {
  if (input === 'acting-as' || input === 'acting_as') return 'login';
  return input;
}
